import { type FC, type ReactNode, useEffect, useLayoutEffect, useState } from "react"
import styled, { css, keyframes } from "styled-components"

import Form from "./Form"
import Portal from "./Portal"

type State = "idle" | "open" | "closed"

interface Props {
	isOpen: boolean
	width?: string | number
	onSubmit?(): void
	children: ReactNode
}

const Modal: FC<Props> = ({ isOpen, width, onSubmit, children }) => {
	const [state, setState] = useState<State>(isOpen ? "open" : "idle")

	useLayoutEffect(() => {
		if (!isOpen && state === "idle") return
		setState(isOpen ? "open" : "closed")
	}, [isOpen, state])

	useEffect(() => {
		if (state !== "closed") return

		const timeout = setTimeout(() => {
			setState("idle")
		}, 300)

		return () => clearTimeout(timeout)
	}, [state])

	if (state === "idle") return null

	return (
		<Portal>
			<Container $isOpen={isOpen}>
				{onSubmit ? (
					<Frame as={Form} $isOpen={isOpen} $width={width} onSubmit={onSubmit}>
						{children}
					</Frame>
				) : (
					<Frame $isOpen={isOpen} $width={width}>
						{children}
					</Frame>
				)}
			</Container>
		</Portal>
	)
}

const Container = styled.div<{ $isOpen: boolean }>`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1000;
	background-color: ${props => (props.$isOpen ? "rgba(28, 28, 28, 0.6)" : "transparent")};
	backdrop-filter: ${props => (props.$isOpen ? "blur(8px)" : "none")};
	pointer-events: ${props => (props.$isOpen ? "all" : "none")};
	display: flex;
	justify-content: center;
	align-items: center;
	transition: 0.15s background-color;
`

const openAnimation = keyframes`
	from {
		transform: scale(0);
	}
	to {
		transform: scale(1);
	}
`

const closeAnimation = keyframes`
	from {
		transform: scale(1);
	}
	to {
		transform: scale(0);
	}
`

const Frame = styled.div<{ $isOpen: boolean; $width?: string | number }>`
	${props =>
		props.$width !== undefined &&
		css`
			width: ${typeof props.$width === "number" ? `${props.$width}px` : props.$width};
			max-width: 100%;
		`}
	max-height: 95vh;
	overflow-y: auto;
	background-color: white;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	transform: scale(0);
	animation: ${props => (props.$isOpen ? openAnimation : closeAnimation)} 0.15s forwards;
`

export default Modal
